import type {
	PortalBackInStockTextChoiceDto,
	PortalBackInStockTextChoiceResponseDto,
	PortalNotificationListResponseDto,
	PortalNotificationMutationResponseDto,
	PortalNotificationPreferencesDto,
	PortalNotificationUnreadCountDto,
	PortalPushPublicKeyDto,
	PortalPushSubscriptionDto,
	PortalSupportNotifyPreferencesDto,
	UpdatePortalNotificationPreferencesDto,
	UpdatePortalSupportNotifyPreferencesDto,
} from '@supply-mover-2-0/shared';
import { PORTAL_ORDER_APPROVAL_PATH } from '@supply-mover-2-0/shared';
import type { NotificationsDataSourceModel } from '@supply-mover-2-0/web-ui';
import { portalFetch } from '../lib/apiClient';
import { usePortalAuthStore } from '../stores/portalAuthStore';

/**
 * PD-16475 — the storefront notification center API
 * (`/api/portal/notifications`). Every call acts on the signed-in customer's
 * own notifications; the server resolves who that is from the token.
 */

export async function listPortalNotifications(
	token: string | null,
	page: { limit?: number; offset?: number } = {},
): Promise<PortalNotificationListResponseDto> {
	const params = new URLSearchParams();
	if (page.limit != null) {
		params.set('limit', String(page.limit));
	}
	if (page.offset != null) {
		params.set('offset', String(page.offset));
	}
	const qs = params.toString();
	return portalFetch<PortalNotificationListResponseDto>(
		`/notifications${qs !== '' ? `?${qs}` : ''}`,
		token,
	);
}

export async function getPortalUnreadCount(
	token: string | null,
): Promise<PortalNotificationUnreadCountDto> {
	return portalFetch<PortalNotificationUnreadCountDto>(
		'/notifications/unread-count',
		token,
	);
}

export async function markPortalNotificationRead(
	token: string | null,
	notificationId: number,
): Promise<PortalNotificationMutationResponseDto> {
	return portalFetch<PortalNotificationMutationResponseDto>(
		`/notifications/${notificationId}/read`,
		token,
		{ method: 'POST', body: JSON.stringify({}) },
	);
}

export async function markAllPortalNotificationsRead(
	token: string | null,
): Promise<PortalNotificationMutationResponseDto> {
	return portalFetch<PortalNotificationMutationResponseDto>(
		'/notifications/read-all',
		token,
		{ method: 'POST', body: JSON.stringify({}) },
	);
}

export async function dismissPortalNotification(
	token: string | null,
	notificationId: number,
): Promise<PortalNotificationMutationResponseDto> {
	return portalFetch<PortalNotificationMutationResponseDto>(
		`/notifications/${notificationId}/dismiss`,
		token,
		{ method: 'POST', body: JSON.stringify({}) },
	);
}

export async function getPortalNotificationPreferences(
	token: string | null,
): Promise<PortalNotificationPreferencesDto> {
	return portalFetch<PortalNotificationPreferencesDto>(
		'/notifications/preferences',
		token,
	);
}

export async function updatePortalNotificationPreferences(
	token: string | null,
	body: UpdatePortalNotificationPreferencesDto,
): Promise<PortalNotificationPreferencesDto> {
	return portalFetch<PortalNotificationPreferencesDto>(
		'/notifications/preferences',
		token,
		{ method: 'PUT', body: JSON.stringify(body) },
	);
}

export async function getPortalSupportNotifyPreferences(
	token: string | null,
): Promise<PortalSupportNotifyPreferencesDto> {
	return portalFetch<PortalSupportNotifyPreferencesDto>(
		'/notifications/support-preferences',
		token,
	);
}

export async function updatePortalSupportNotifyPreferences(
	token: string | null,
	body: UpdatePortalSupportNotifyPreferencesDto,
): Promise<PortalSupportNotifyPreferencesDto> {
	return portalFetch<PortalSupportNotifyPreferencesDto>(
		'/notifications/support-preferences',
		token,
		{ method: 'PUT', body: JSON.stringify(body) },
	);
}

/**
 * PD-14306 — the customer's answer to the one-time "also text me?" question
 * asked after their first "Notify me". Yes records text consent for their
 * phone; No is remembered so the question is not asked again.
 */
export async function answerBackInStockTextPrompt(
	token: string | null,
	body: PortalBackInStockTextChoiceDto,
): Promise<PortalBackInStockTextChoiceResponseDto> {
	return portalFetch<PortalBackInStockTextChoiceResponseDto>(
		'/notifications/back-in-stock-text',
		token,
		{ method: 'PUT', body: JSON.stringify(body) },
	);
}

export async function getPortalPushPublicKey(
	token: string | null,
): Promise<PortalPushPublicKeyDto> {
	return portalFetch<PortalPushPublicKeyDto>(
		'/notifications/push-public-key',
		token,
	);
}

export async function registerPortalPushSubscription(
	token: string | null,
	subscription: PortalPushSubscriptionDto,
): Promise<PortalNotificationMutationResponseDto> {
	return portalFetch<PortalNotificationMutationResponseDto>(
		'/notifications/push-subscription',
		token,
		{ method: 'POST', body: JSON.stringify(subscription) },
	);
}

export async function unregisterPortalPushSubscription(
	token: string | null,
	endpoint: string,
): Promise<PortalNotificationMutationResponseDto> {
	return portalFetch<PortalNotificationMutationResponseDto>(
		'/notifications/push-subscription',
		token,
		{ method: 'DELETE', body: JSON.stringify({ endpoint }) },
	);
}

/** The signed-in token at call time (portalFetch refreshes it when needed). */
function currentToken(): string | null {
	return usePortalAuthStore.getState().token;
}

/**
 * The storefront's data source for the shared bell (libs/web-ui
 * NotificationsPanel). A module-level constant so the bell's shared unread
 * poll is keyed once ('portal') and never mixes with the CRM's.
 */
export const portalNotificationsSource: NotificationsDataSourceModel = {
	key: 'portal',
	list: async ({ limit }) =>
		(await listPortalNotifications(currentToken(), { limit })).notifications,
	getUnreadCount: async () =>
		(await getPortalUnreadCount(currentToken())).count,
	markRead: async (notificationId) => {
		await markPortalNotificationRead(currentToken(), notificationId);
	},
	markAllRead: async () => {
		await markAllPortalNotificationsRead(currentToken());
	},
	dismiss: async (notificationId) => {
		await dismissPortalNotification(currentToken(), notificationId);
	},
};

/** Where a storefront notification opens. */
export function resolvePortalNotificationPath(
	entityType: string | null,
	entityId: number | null,
): string | null {
	if (entityType == null || entityId == null) {
		return null;
	}
	switch (entityType) {
		case 'issue':
			return `/support/${entityId}`;
		// PD-16845 — a cart another buyer asked this one to approve. The
		// Awaiting Approval tab switches to the cart's account and opens it.
		case 'order_approval':
			return PORTAL_ORDER_APPROVAL_PATH;
		// PD-16863 — a new statement; the detail page reads it whichever of
		// the user's accounts it belongs to.
		case 'statement':
			return `/statements/${entityId}`;
		// PD-14306 — an item the customer asked about is back in stock; open
		// its product page.
		case 'item':
			return `/product/${entityId}`;
		default:
			return null;
	}
}
